/**
 * Level loader: turns a JSON LevelDef into scene objects, colliders, and the
 * lookup tables the systems need.
 *
 * Note the two coordinate conventions being applied here (see schema.ts):
 * geometry entries are CENTER+SIZE, props are BASE positions. All of the
 * conversion happens in this file, so nothing downstream has to think about it.
 */

import * as THREE from 'three';
import type { LevelDef, GeometryDef } from './schema';
import { parseLevel, roomIdAt } from './schema';
import type { Vec3 } from '@/core/math';
import { vec3 } from '@/core/math';
import type { PhysicsWorld } from '@/engine/physicsWorld';
import type { PropSystem } from '@/world/prop';
import { makeToonMaterial } from '@/render/toonMaterial';
import { addOutline } from '@/render/outline';
import { createAssetObject } from '@/engine/assetRegistry';
import type { HideSpotRef } from '@/ai/granny';

export interface CatMomentInstance {
  id: string;
  kind: string;
  position: Vec3;
  cozyPoints: number;
  completed: boolean;
}

export interface KeyInstance {
  id: string;
  flavor: 'plain' | 'mischief' | 'granny';
  position: Vec3 | null;
  /** Prop whose destruction reveals this key. */
  revealedBy?: string;
  collected: boolean;
  /** Hidden until revealed (mischief keys) or until dropped (granny keys). */
  available: boolean;
  object: THREE.Object3D | null;
}

export interface LoadedLevel {
  def: LevelDef;
  hideSpots: HideSpotRef[];
  catOnlyVolumes: { center: Vec3; size: Vec3 }[];
  keys: KeyInstance[];
  catMoments: CatMomentInstance[];
  roomIdAt: (x: number, z: number) => string | undefined;
  /**
   * Hides a completed moment's marker. The markers share one InstancedMesh, so
   * "hiding" one means collapsing its instance matrix.
   */
  setCatMomentCompleted: (momentId: string) => void;
  /** Everything added to the scene, for teardown. */
  objects: THREE.Object3D[];
}

export function loadLevel(
  data: unknown,
  deps: { scene: THREE.Scene; physics: PhysicsWorld; props: PropSystem },
  sourceName = 'level',
): LoadedLevel {
  const def = parseLevel(data, sourceName);
  const objects: THREE.Object3D[] = [];
  const catOnlyVolumes: { center: Vec3; size: Vec3 }[] = [];

  if (def.skyColor) deps.scene.background = new THREE.Color(def.skyColor);

  // --- Geometry -------------------------------------------------------
  for (const geo of def.geometry) {
    const center = vec3(geo.position[0], geo.position[1], geo.position[2]);
    const size = vec3(geo.size[0], geo.size[1], geo.size[2]);

    // catOnly entries are NAVMESH EXCLUSIONS, not solid geometry: no collider,
    // no mesh. The cat runs straight through; granny steers around (see
    // Granny.avoidCatOnly). Rendering them would be actively confusing.
    if (geo.catOnly) {
      catOnlyVolumes.push({ center, size });
      continue;
    }

    const mesh = buildGeometryMesh(geo, size);
    mesh.position.set(center.x, center.y, center.z);
    if (geo.rotationY) mesh.rotation.y = geo.rotationY;
    deps.scene.add(mesh);
    objects.push(mesh);

    deps.physics.addStaticBox({
      position: center,
      size,
      rotationY: geo.rotationY,
      sightBlocker: geo.sightBlocker ?? geo.kind === 'wall',
    });
  }

  // --- Props ----------------------------------------------------------
  for (const prop of def.props) deps.props.spawn(prop);

  // --- Hide spots -----------------------------------------------------
  const hideSpots: HideSpotRef[] = def.hideSpots.map((spot) => ({
    id: spot.id,
    position: vec3(spot.position[0], spot.position[1], spot.position[2]),
    size: vec3(spot.size[0], spot.size[1], spot.size[2]),
  }));

  // --- Keys -----------------------------------------------------------
  const keys: KeyInstance[] = def.keys.map((key) => {
    const position = key.position ? vec3(key.position[0], key.position[1], key.position[2]) : null;

    // Mischief keys stay hidden until their prop breaks; granny keys ride in her
    // apron and only become grabbable when she drops them.
    const available = key.flavor === 'plain';

    let object: THREE.Object3D | null = null;
    if (position) {
      object = createAssetObject('prop.key');
      object.position.set(position.x, position.y, position.z);
      object.name = `key:${key.id}`;
      object.visible = available;
      addOutlineToAll(object);
      deps.scene.add(object);
      objects.push(object);
    }

    return {
      id: key.id,
      flavor: key.flavor,
      position,
      ...(key.revealedBy ? { revealedBy: key.revealedBy } : {}),
      collected: false,
      available,
      object,
    };
  });

  // --- Cat Moments ----------------------------------------------------
  const catMoments: CatMomentInstance[] = def.catMoments.map((moment) => ({
    id: moment.id,
    kind: moment.kind,
    position: vec3(moment.position[0], moment.position[1], moment.position[2]),
    cozyPoints: moment.cozyPoints ?? 25,
    completed: false,
  }));

  // One InstancedMesh for every marker rather than one mesh each. Levels are
  // meant to be scattered densely with these (DESIGN.md 6), so per-marker meshes
  // would make the "just be a cat" track cost draw calls in proportion to how
  // much of it a level offers - exactly backwards.
  const momentMarkers = buildMomentMarkers(catMoments);
  deps.scene.add(momentMarkers);
  objects.push(momentMarkers);

  const markerScratch = new THREE.Matrix4();

  return {
    def,
    hideSpots,
    catOnlyVolumes,
    keys,
    catMoments,
    roomIdAt: (x, z) => roomIdAt(def, x, z),
    setCatMomentCompleted: (momentId) => {
      const index = catMoments.findIndex((moment) => moment.id === momentId);
      if (index < 0) return;
      // Collapsed to nothing: a moment you have already had should stop asking
      // for your attention.
      momentMarkers.setMatrixAt(index, markerScratch.makeScale(0, 0, 0));
      momentMarkers.instanceMatrix.needsUpdate = true;
    },
    objects,
  };
}

function buildGeometryMesh(geo: GeometryDef, size: Vec3): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
  const color = geo.color ?? defaultColorFor(geo.kind);
  const mesh = new THREE.Mesh(geometry, makeToonMaterial(color));
  mesh.name = `geo:${geo.id}`;
  mesh.receiveShadow = true;
  // Floors and walls do not cast: with one directional light, a wall casting
  // onto itself just produces acne, and it doubles the shadow-map cost.
  mesh.castShadow = geo.kind === 'furniture';

  // Only furniture gets an outline. Outlining the floor and walls would put a
  // black line around the entire room, which reads as a rendering bug.
  if (geo.kind === 'furniture') addOutline(mesh);
  return mesh;
}

function defaultColorFor(kind: GeometryDef['kind']): string {
  switch (kind) {
    case 'floor':
      return '#c98f5a';
    case 'wall':
      return '#e8d5b7';
    case 'ceiling':
      return '#f2e8d8';
    case 'furniture':
      return '#8b5a2b';
  }
}

/** Soft glowing discs, one instance each. Replaced by real props in the art pass. */
function buildMomentMarkers(moments: readonly CatMomentInstance[]): THREE.InstancedMesh {
  const geometry = new THREE.RingGeometry(0.13, 0.2, 18);
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({
    color: '#9ee7ff',
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });

  // InstancedMesh needs a non-zero count even for an empty level.
  const mesh = new THREE.InstancedMesh(geometry, material, Math.max(1, moments.length));
  mesh.name = 'moments:markers';
  mesh.count = moments.length;
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  const matrix = new THREE.Matrix4();
  moments.forEach((moment, index) => {
    matrix.makeTranslation(moment.position.x, moment.position.y + 0.1, moment.position.z);
    mesh.setMatrixAt(index, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

function addOutlineToAll(root: THREE.Object3D): void {
  root.traverse((node) => {
    if (node instanceof THREE.Mesh && !node.name.endsWith(':outline')) addOutline(node);
  });
}

export function unloadLevel(level: LoadedLevel, scene: THREE.Scene): void {
  for (const object of level.objects) {
    scene.remove(object);
    object.traverse((node) => {
      if (node instanceof THREE.Mesh) node.geometry.dispose();
    });
  }
  level.objects.length = 0;
}
